import React, { useState, useEffect } from "react";
import CustomModal from "../../../../../../components/CustomModal";
import InputCustom from "../../../../../../components/InputCustom";
import api from "../../../../../../services/api";
import type { IResumoFechamento } from "./pdf";

interface Props {
  onClose: () => void;
  onConfirm: (data: { valorFechamento: number; justificativa: string; dataFechamento: string }) => void;
  data?: any; // Pass data for summary
}

const ModalFechamento: React.FC<Props> = ({ onClose, onConfirm, data }) => {
  const [valorFechamento, setValorFechamento] = useState<string>("");
  const [justificativa, setJustificativa] = useState<string>("");
  const [dataFechamento] = useState<string>(new Date().toLocaleString('pt-BR'));
  const [resumo, setResumo] = useState<IResumoFechamento | null>(null);

  useEffect(() => {
    if (data?.id_operacao_patio_guiche_movimentacao) {
      fetchResumo(data.id_operacao_patio_guiche_movimentacao);
    }
  }, [data]);

  const fetchResumo = async (id: number) => {
    try {
      const response = await api.post('/operacaoPatioGuichesMovimentacao/resumo', {
        id_operacao_patio_guiche_movimentacao: id
      });
      setResumo(response.data);
      if (response.data?.valor_caixa !== undefined) {
         const formattedValue = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(response.data.valor_caixa);
         setValorFechamento(formattedValue);
      }
    } catch (error) {
      console.error("Erro ao buscar resumo", error);
    }
  };

  const formatCurrency = (value: string) => {
    const numericValue = value.replace(/\D/g, "");
    const number = Number(numericValue) / 100;
    return number.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  };

  const handleChangeValor = (value: string) => {
    setValorFechamento(formatCurrency(value));
  };

  const formatResumoCurrency = (value: number | string | null | undefined) => {
    const numericValue = Number(value ?? 0);

    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(Number.isNaN(numericValue) ? 0 : numericValue);
  };

  const entradaRows = [
    { label: "Valor na Abertura", value: resumo?.valor_abertura },
    { label: "Dinheiro", value: resumo?.total_dinheiro },
    { label: "PIX", value: resumo?.total_pix },
    { label: "Cartão de Crédito", value: resumo?.total_cartao_credito },
    { label: "Cartão de Débito", value: resumo?.total_cartao_debito },
    { label: "Cartão Manual Crédito", value: resumo?.total_cartao_manual_credito },
    { label: "Cartão Manual Débito", value: resumo?.total_cartao_manual_debito },
    { label: "Faturado", value: resumo?.total_faturado },
    { label: "Descontos", value: resumo?.total_descontos },
  ];

  const saidaRows = [
    { label: "Sangrias", value: resumo?.total_sangrias },
  ];

  const ajusteRows = [
    { label: "Total Estornado", value: resumo?.total_estornado },
  ];

  const handleConfirm = () => {
    // Remove tudo exceto dígitos e vírgula, converte vírgula em ponto
    // (Intl.NumberFormat usa espaço não-separável \u00a0 que trim() não remove)
    const numericValue = Number.parseFloat(
      valorFechamento.replace(/[^\d,]/g, "").replace(",", ".")
    );
    if (Number.isNaN(numericValue)) {
      return;
    }
    onConfirm({
        valorFechamento: numericValue,
        justificativa,
        dataFechamento
    });
  };

  return (
    <CustomModal title="Fechamento" onClose={onClose} isScreenLg>
      <div className="flex flex-col w-full max-h-[85vh] bg-[#F5F5F5]">
        
        <div className="p-6 flex flex-col gap-6 overflow-y-auto flex-1">
            {/* Resumo Section */}
            <div className="flex gap-8">
                <div className="w-1/3">
                    <h3 className="font-bold text-gray-800">Resumo</h3>
                    <p className="text-sm text-gray-500">Somatório das movimentações</p>
                </div>
                <div className="w-2/3 bg-white p-6 rounded-md shadow-sm">
                    <h4 className="text-[#062D4E] font-bold mb-4 border-b pb-2">Resumo</h4>
                    
                    <div className="space-y-2 text-sm">
                        <div className="font-bold">Entradas</div>
                        {entradaRows.map((row) => (
                            <div className="flex justify-between gap-4" key={row.label}>
                                <span>{row.label}</span>
                                <span>{formatResumoCurrency(row.value)}</span>
                            </div>
                        ))}

                        <div className="font-bold mt-4">Saídas</div>
                        {saidaRows.map((row) => (
                            <div className="flex justify-between gap-4" key={row.label}>
                                <span>{row.label}</span>
                                <span>{formatResumoCurrency(row.value)}</span>
                            </div>
                        ))}

                        <div className="font-bold mt-4">Ajustes</div>
                        {ajusteRows.map((row) => (
                          <div className="flex justify-between gap-4" key={row.label}>
                            <span>{row.label}</span>
                            <span>{formatResumoCurrency(row.value)}</span>
                          </div>
                        ))}
                        
                        <div className="flex justify-between font-bold text-green-600 mt-2">
                            <span>Total Entradas</span>
                            <span>{formatResumoCurrency(resumo?.total_entradas)}</span>
                        </div>
                        <div className="flex justify-between font-bold text-red-600">
                            <span>Total das Saída</span>
                            <span>{formatResumoCurrency(resumo?.total_saidas)}</span>
                        </div>
                        <div className="flex justify-between font-bold text-[#062D4E] text-lg mt-2 border-t pt-2">
                            <span>Valor no Caixa</span>
                            <span>{formatResumoCurrency(resumo?.valor_caixa)}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Fechamento Section */}
            <div className="flex gap-8">
                <div className="w-1/3">
                    <h3 className="font-bold text-gray-800">Fechamento do caixa</h3>
                    <p className="text-sm text-gray-500">Informações do Fechamento do Caixa</p>
                </div>
                <div className="w-2/3 bg-white p-6 rounded-md shadow-sm space-y-4">
                    <div>
                        <InputCustom
                            title="Data de Fechamento"
                            value={dataFechamento}
                            disabled
                            onChange={() => {}}
                            placeholder=""
                        />
                    </div>
                    <div>
                        <InputCustom
                            title="Valor do Caixa Fechamento"
                            value={valorFechamento}
                            onChange={handleChangeValor}
                            placeholder="R$ 0,00"
                        />
                    </div>
                    <div>
                        <InputCustom
                            title="Justificativa do Fechamento"
                            value={justificativa}
                            onChange={(value) => setJustificativa(value)}
                            placeholder=""
                        />
                    </div>
                </div>
            </div>
        </div>

        <div className="flex justify-end p-4 gap-2 bg-white border-t shrink-0">
            <button onClick={onClose} className="w-20 h-8 rounded-full bg-[#F9FAFA] text-xs text-[#000] font-bold border border-[#DBDEDF]">Cancelar</button>
            <button onClick={handleConfirm} className="w-20 h-8 rounded-full bg-[#0C4A85] text-xs text-[#fff] font-bold">Salvar</button>
        </div>
      </div>
    </CustomModal>
  );
};

export default ModalFechamento;
